import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RotateCcw, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DeleteConfirmDialog } from "@/components/ui/delete-confirm-dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { api } from "@shared/routes";
import type { RecurrenceRule } from "@shared/general-task-recurrence";

const RECURRING_URL = "/api/admin/general-tasks/recurring";
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

type RecurringTemplate = {
  id: number;
  title: string;
  apartmentName: string | null;
  assignedToName: string | null;
  recurrenceRule: RecurrenceRule | null;
  recurrenceEndDate: string | null;
  nextOccurrenceAt: string | null;
};

type DeleteSeriesResult = { templateId: number; deletedPending: number; keptHistory: number };

function describeRule(rule: RecurrenceRule | null): string {
  if (!rule) return "No schedule";
  const time = rule.timeOfDay ? ` at ${rule.timeOfDay}` : "";
  const days = rule.daysOfWeek?.length ? ` on ${rule.daysOfWeek.map(d => DAY_NAMES[d] ?? d).join(", ")}` : "";
  if (rule.frequency === "daily") return `Every day${time}`;
  if (rule.frequency === "weekly") return `Weekly${days}${time}`;
  if (rule.frequency === "biweekly") return `Every 2 weeks${days}${time}`;
  return `Monthly on day ${rule.dayOfMonth ?? 1}${time}`;
}

function shortDate(iso: string | null): string {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/**
 * Lists the hidden templates that generate recurring general tasks and lets the admin stop a
 * series. Deleting a single generated task never stops the series; this is the only place that does.
 */
export function RecurringSeriesCard() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const { data: templates = [] } = useQuery<RecurringTemplate[]>({ queryKey: [RECURRING_URL] });

  const stopSeries = useMutation({
    mutationFn: async (id: number): Promise<DeleteSeriesResult> => {
      const res = await apiRequest("DELETE", `/api/admin/general-tasks/${id}/series`);
      return (await res.json()) as DeleteSeriesResult;
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: [RECURRING_URL] });
      void queryClient.invalidateQueries({ queryKey: [api.admin.generalTasks.list.path] });
      toast({
        title: "Recurring series stopped",
        description: `${result.deletedPending} pending task(s) removed. ${result.keptHistory} completed or in-progress task(s) kept.`,
      });
    },
    onError: (err: Error) => toast({ title: err.message || "Failed to stop series", variant: "destructive" }),
  });

  if (templates.length === 0) return null;
  const active = (t: RecurringTemplate) => t.nextOccurrenceAt != null;

  return (
    <Card data-testid="card-recurring-series">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <RotateCcw className="w-4 h-4 text-blue-600" />
          Recurring series
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          These schedules create the repeating tasks below. Deleting one task only removes that day. To stop it from coming back, stop the series here.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {templates.map(t => (
          <div key={t.id} className="flex items-center justify-between gap-3 border rounded-lg p-3 flex-wrap" data-testid={`row-recurring-${t.id}`}>
            <div className="min-w-0 space-y-0.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-sm">{t.title}</span>
                <Badge variant={active(t) ? "default" : "secondary"}>{active(t) ? "Active" : "Ended"}</Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                {describeRule(t.recurrenceRule)}
                {t.apartmentName ? ` · ${t.apartmentName}` : ""}
                {t.assignedToName ? ` · ${t.assignedToName}` : ""}
              </p>
              <p className="text-xs text-muted-foreground">
                Next: {active(t) ? shortDate(t.nextOccurrenceAt) : "none"} · Ends: {shortDate(t.recurrenceEndDate)}
              </p>
            </div>
            <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => setConfirmId(t.id)} data-testid={`button-stop-series-${t.id}`}>
              <Trash2 className="w-3 h-3 mr-1" /> Stop series
            </Button>
          </div>
        ))}
      </CardContent>
      <DeleteConfirmDialog
        open={confirmId !== null}
        onOpenChange={open => { if (!open) setConfirmId(null); }}
        title="Stop this recurring series?"
        description="No more tasks will be created from it, and its pending tasks will be deleted. Completed and in-progress tasks are kept."
        confirmLabel="Stop series"
        pending={stopSeries.isPending}
        cancelTestId="button-stop-series-cancel"
        confirmTestId="button-stop-series-confirm"
        onConfirm={async () => {
          if (confirmId === null) return;
          await stopSeries.mutateAsync(confirmId).catch(() => undefined);
          setConfirmId(null);
        }}
      />
    </Card>
  );
}

export default RecurringSeriesCard;
